import "./GameDropdown.style.css";

import React, { useState, useRef, useEffect } from "react";
import { CSSTransition } from "react-transition-group";
import { checkSearchValueForObjectName } from "../../../utils/functions/checkSearchValueForObjectName";
import { Input, InputProps } from "../../base/Input/Input";
import { GameObjectState } from "../../../utils/states/GamePageStates/GameObjectState";
import { Button, ButtonColor, ButtonSize } from "../../base/Button/Button";
import { CrossIcon24 } from "../../icons/CrossIcon24";
import { TypeModeIcon24 } from "../../icons/TypeModeIcon24";
import { useIsMobile } from "../../../utils/functions/useIsMobile";

interface GameDropdownProps extends InputProps {
  items?: GameObjectState[];
  showImages?: boolean;
  onItemSelected?: (selectedItem: GameObjectState) => void;
}

export const GameDropdown: React.FC<GameDropdownProps> = ({
  items = [],
  showImages = true,
  ref,
  className = "",
  onItemSelected,
  ...inputRestProps
}: GameDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isMobile = useIsMobile();

  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [value, setValue] = useState<string>("");

  const filteredItems = items.filter((item) =>
    checkSearchValueForObjectName(item.name, value),
  );
  const sortedItems = filteredItems.sort((item1, item2) =>
    item1.name.toLowerCase().localeCompare(item2.name.toLowerCase()),
  );
  const shouldShowDropdown = isDropdownVisible && sortedItems.length > 0;

  const combinedContainerClasses = `dropdown__container ${className}`
    .trim()
    .replace(/\s+/g, " ");

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownVisible(false);
    }
  };

  const handleItemClick = (item: GameObjectState) => {
    if (onItemSelected) {
      onItemSelected(item);
    }
    setIsDropdownVisible(false);
    if (inputRef.current) {
      inputRef.current.value = "";
      setValue("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} className={combinedContainerClasses}>
      <div className="dropdown__input-container">
        <Input
          className="dropdown__input"
          ref={inputRef}
          placeholder={isSearchMode ? "Начни вводить ответ" : "Выбери ответ"}
          inputMode={isSearchMode ? "search" : "none"}
          autoComplete="off"
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsDropdownVisible(true)}
          {...inputRestProps}
        />
        {isMobile && (
          <Button
            className="dropdown__input__search-button"
            color={ButtonColor.Empty}
            size={ButtonSize.Small}
            icon={isSearchMode ? <CrossIcon24 /> : <TypeModeIcon24 />}
            onClick={() => setIsSearchMode(!isSearchMode)}
          />
        )}
      </div>
      <CSSTransition
        in={shouldShowDropdown}
        timeout={300}
        classNames={"dropdown__list_animated"}
        unmountOnExit
        nodeRef={dropdownListRef}
      >
        <ul className="dropdown__list" ref={dropdownListRef}>
          {sortedItems.map((item) => {
            return (
              <li
                key={item.id}
                className="dropdown__list-item"
                onClick={() => handleItemClick(item)}
              >
                {showImages && (
                  <div className="dropdown__list-item__image-wrapper">
                    <img
                      className="dropdown__list-item__image"
                      src={item.image}
                      alt={item.name}
                    />
                  </div>
                )}
                <div className="dropdown__list-item__caption">{item.name}</div>
              </li>
            );
          })}
        </ul>
      </CSSTransition>
    </div>
  );
};
